import { ROUTES } from './config.js';

/**
 * Where to send the operator after they sign in.
 *
 * Normally carried in router state (`location.state.from`), which survives a refresh
 * of the login page. Google OAuth is a full-page round trip that always lands back
 * on `/`, so for that path the target is also parked in sessionStorage — per tab,
 * like the campaign draft — and picked up once the app is authenticated again.
 */

const STORAGE_KEY = 'opterite:post-login-redirect';
const PARSE_BASE = 'http://internal.invalid';

/**
 * Returns an in-app path, or null if the value is unsafe or pointless.
 *
 * Rejects anything that could leave the app (absolute URLs, protocol-relative
 * `//host`, backslash tricks the URL parser normalises into those) and the login
 * route itself, which would otherwise bounce the operator straight back to it.
 * `?error=` is dropped: it is the OAuth failure flag and would re-show a stale error.
 */
export function sanitizeRedirectPath(value) {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\')) return null;

  let url;
  try {
    url = new URL(trimmed, PARSE_BASE);
  } catch {
    return null;
  }

  // The parser strips tabs and newlines, so `/\t/evil.example` becomes
  // protocol-relative. Checking the resolved origin catches every such variant.
  if (url.origin !== PARSE_BASE) return null;

  if (url.pathname === ROUTES.login || url.pathname.startsWith(`${ROUTES.login}/`)) return null;

  url.searchParams.delete('error');

  return `${url.pathname}${url.search}${url.hash}`;
}

/** Path + query + hash of a react-router location. */
export function pathOfLocation(location) {
  if (!location) return null;
  return `${location.pathname ?? ''}${location.search ?? ''}${location.hash ?? ''}`;
}

export function rememberPostLoginRedirect(path) {
  const safe = sanitizeRedirectPath(path);
  try {
    if (safe) window.sessionStorage.setItem(STORAGE_KEY, safe);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable; the operator lands on the dashboard instead */
  }
}

/** Reads and clears the parked target, so it is honoured at most once. */
export function consumePostLoginRedirect() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    window.sessionStorage.removeItem(STORAGE_KEY);
    return sanitizeRedirectPath(raw);
  } catch {
    return null;
  }
}
